import {
  EditorClose,
  editorSaved,
} from 'src/app/shared/utils/editor-close.utils';
import {
  Input,
  computed,
  inject,
  signal,
  Injector,
  OnInit,
  ViewChild,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  NgForm,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';

import { Store, select } from '@ngrx/store';

import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { ObjectEditorService } from '../../../../core/services/object-editor.service';

import { NomObjectClass, NomObjectTypes } from '../../../../core/constants';
import { DataObject } from '../../../../core/models/core.models';

import { showShadow, hideShadow } from '../../../../core/state/core.actions';

import {
  selectCurrentCompanyId,
  selectHasShadow,
  selectUser,
} from '../../../../core/state';
import { AppState } from 'src/app/core/state/app.state';
import {
  RecentObjectsScope,
  injectRecentObjectsQuery,
} from 'src/app/api/queries/user.queries';
import { injectUserObjectSearchQuery } from 'src/app/api/queries/object.queries';
import { UserObjectSearchCriteria } from 'src/app/api/services/user-api.service';
import { RecentObjectsService } from '../../../../core/services/recent-objects.service';

import { Observable, fromEvent } from 'rxjs';
import { debounceTime, map, tap } from 'rxjs/operators';
import { NgClass, AsyncPipe } from '@angular/common';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../../../../shared/components/loading/loading.component';

import { TranslatePipe } from '@ngx-translate/core';
import { HighlightPipe } from '../../../../shared/pipes/highlight.pipe';

export interface SuggestItem {
  class_id: number;
  id: string;
  suggest: string;
  active: boolean;
  icon: IconProp;
  workspace_id?: string;
}

export interface RecentSuggestItem {
  object_class_id: number;
  id: string;
  title: string;
  active: boolean;
  icon: IconProp;
  workspace_id?: string;
}

@Component({
  selector: 'app-search-suggest',
  templateUrl: './search-suggest.component.html',
  styleUrls: ['./search-suggest.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    FaIconComponent,
    LoadingComponent,
    AsyncPipe,
    TranslatePipe,
    HighlightPipe,
  ],
})
export class SearchSuggestComponent implements OnInit {
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  @Input() searchStyle: string = 'default';

  searchSuggestions: any[] = [];
  searchSuggestionsActive = false;

  recentObjects: DataObject[] = [];
  recentObjectsMode: boolean = true;

  suggestRecentSearches: string[] = [];
  searchRecommendationLoaded = false;
  searchQuery = '';
  advanceFiltersVisible = false;
  typedQuery: string = '';
  selectedSuggestIndex = 0;
  searchForm: FormGroup;
  isLoading: boolean = false;
  hasShadow: boolean = false;

  recentObjects$: Observable<DataObject[]>;
  suggestionsLoading$: Observable<boolean>;
  hasShadow$: Observable<boolean>;

  /** The term the "all matches" query is keyed on; empty disables it. */
  private readonly searchTerm = signal('');
  private searchQueryRef!: ReturnType<typeof injectUserObjectSearchQuery>;

  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private recentObjectsService = inject(RecentObjectsService);

  constructor(
    protected fb: FormBuilder,
    protected eRef: ElementRef,
    protected store: Store<AppState>,
    protected router: Router,
    private editService: ObjectEditorService,
  ) {
    this.searchForm = fb.group({
      query: new FormControl(),
    });

    // This component lives in the header, so it is the app's long-lived observer of
    // the key: the query fetches once per user + company and is patched in place.
    const user = this.store.selectSignal(selectUser);
    const companyId = this.store.selectSignal(selectCurrentCompanyId);

    const recentObjectsScope = computed<RecentObjectsScope | null>(() => {
      const userId = user()?.id;

      return userId ? { userId, companyId: companyId() } : null;
    });

    const recentObjectsQuery = injectRecentObjectsQuery(recentObjectsScope);

    this.recentObjects$ = toObservable(
      computed(() => recentObjectsQuery.data() ?? []),
    );

    // The term is the query key, so an empty one disables the query rather than
    // searching for everything.
    this.searchQueryRef = injectUserObjectSearchQuery(
      computed<UserObjectSearchCriteria | null>(() => {
        const query = this.searchTerm();

        return query
          ? {
              query,
              workspaceId: null,
              objectClassIds: [
                NomObjectClass.FEED,
                NomObjectClass.WORKSPACE,
                NomObjectClass.DASHBOARD,
              ],
              objectTypeIds: null,
            }
          : null;
      }),
    );

    this.suggestionsLoading$ = toObservable(
      computed(() => this.searchQueryRef.isFetching()),
    );

    this.hasShadow$ = this.store.pipe(select(selectHasShadow));

    this.hasShadow$
      .pipe(
        tap((hasShadow) => {
          this.hasShadow = hasShadow;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.recentObjects$
      .pipe(
        map((objects) => objects.map((object) => ({ ...object }))),
        tap((recentObjects) => {
          this.recentObjects = recentObjects;

          if (this.recentObjectsMode === true) {
            this.searchSuggestions = recentObjects.map((object) => {
              return {
                ...object,
                active: false,
              };
            });

            this.selectedSuggestIndex = 0;

            // Ensure searchSuggestions is not empty before setting active
            if (this.searchSuggestions.length > 0) {
              this.selectedSuggestIndex = 0;
              this.searchSuggestions[this.selectedSuggestIndex].active = true;
            } else {
              this.selectedSuggestIndex = -1; // No valid selection
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngOnInit(): void {
    toObservable(
      computed(() => this.searchQueryRef.data() ?? []),
      {
        injector: this.injector,
      },
    )
      .pipe(
        map((objects) => objects.map((object) => ({ ...object }))), // clone each object
        tap((suggestions) => {
          if (this.recentObjectsMode === false) {
            this.searchSuggestions = suggestions;
            this.selectedSuggestIndex = 0;
            if (this.searchSuggestions.length > 0) {
              this.searchSuggestions[this.selectedSuggestIndex].active = true;
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  ngAfterViewInit(): void {
    fromEvent(this.searchInput.nativeElement, 'input')
      .pipe(
        debounceTime(300),
        map((event: Event) => (event.target as HTMLInputElement).value),
        tap((value) => this.searchTerm.set(value)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    // Check if the click happened outside the component
    const clickedInside = this.eRef.nativeElement.contains(event.target);

    if (!clickedInside) {
      this.deactivateSearchSuggest();
    }
  }
  removeFocus(): void {
    if (this.searchInput) {
      this.searchInput.nativeElement.blur(); // Remove focus from input
    }
  }

  onKeyUp(query: string, event: KeyboardEvent) {
    this.typedQuery = query;

    if (this.typedQuery.length === 0) {
      if (this.recentObjectsMode === false) {
        this.searchSuggestions = this.recentObjects.map((object) => {
          return {
            ...object,
            active: false,
          };
        });
        this.selectedSuggestIndex = 0;
        this.searchSuggestions[this.selectedSuggestIndex].active = true;
        this.searchSuggestionsActive = true;
        this.recentObjectsMode = true;
      }
    } else {
      this.recentObjectsMode = false;
    }

    const isNavigationKey = this.isNavigationKeyEvent(event);
    if (!isNavigationKey) {
      this.activateSearchSuggest();
    }

    this.keyUpHandle(event);
  }

  private isNavigationKeyEvent(event: KeyboardEvent): boolean {
    // Define the key codes for navigation keys
    const navigationKeys = [38, 40, 13]; // Up, Down, Enter keys
    return navigationKeys.includes(event.keyCode);
  }

  search(form: NgForm) {
    this.changeSearchQuery(form.value.query);
    this.deactivateSearchSuggest();
  }

  onFocus() {
    this.activateSearchSuggest();
    if (this.typedQuery && this.typedQuery.length > 0) {
      // Re-search on focus. The term has not changed, so neither has the key -
      // only an explicit refetch will do it.
      this.searchTerm.set(this.typedQuery);
      void this.searchQueryRef.refetch();
    }
  }

  /**
   * Nothing invalidates the search or recent-objects queries on a feed save, and
   * this component outlives the dialog - re-fetch both once the editor saved.
   */
  private refreshWhenSaved(close: EditorClose): void {
    editorSaved(close).then((saved) => {
      if (!saved) {
        return;
      }
      void this.searchQueryRef?.refetch();
      this.recentObjectsService.invalidate();
    });
  }

  selectSuggestion(suggestion: DataObject) {
    //console.log(suggestion);
    switch (suggestion.object_class_id) {
      case NomObjectClass.FEED:
        if (suggestion.object_type_id == NomObjectTypes.SEARCH_DIMENSION) {
          this.refreshWhenSaved(
            this.editService.loadFeedAndOpenPopup(suggestion.id),
          );
        } else {
          this.goToFeed(suggestion.workspace_id, suggestion.id);
        }
        break;
      case NomObjectClass.DASHBOARD:
        this.goToDashboard(suggestion.workspace_id, suggestion.id);
        break;
      case NomObjectClass.WORKSPACE:
        this.router.navigate(['analytics', 'workspace', suggestion.id]);
        break;
    }
    this.deactivateSearchSuggest();
    this.searchQuery = '';
    this.typedQuery = '';
  }

  toggleAdvancedFilters() {
    this.advanceFiltersVisible = !this.advanceFiltersVisible;
  }

  keyUpHandle(event: KeyboardEvent) {
    const isValidIndex = (index: number) =>
      index >= 0 && index < this.searchSuggestions.length;

    switch (event.key) {
      case 'ArrowDown':
        if (isValidIndex(this.selectedSuggestIndex + 1)) {
          this.updateActiveSuggestion(
            this.selectedSuggestIndex,
            this.selectedSuggestIndex + 1,
          );
        }
        break;

      case 'ArrowUp':
        if (isValidIndex(this.selectedSuggestIndex - 1)) {
          this.updateActiveSuggestion(
            this.selectedSuggestIndex,
            this.selectedSuggestIndex - 1,
          );
        }
        break;

      case 'Enter':
        if (isValidIndex(this.selectedSuggestIndex)) {
          this.selectSuggestion(
            this.searchSuggestions[this.selectedSuggestIndex],
          );
        }
        break;
      case 'Escape':
        this.deactivateSearchSuggest();
        break;
    }
  }

  updateActiveSuggestion(oldIndex: number, newIndex: number) {
    if (oldIndex !== -1) {
      this.searchSuggestions[oldIndex].active = false;
    }
    this.searchSuggestions[newIndex].active = true;
    this.selectedSuggestIndex = newIndex;
  }

  deactivateSearchSuggest() {
    this.searchSuggestionsActive = false;
    if (this.hasShadow) {
      this.store.dispatch(hideShadow());
    }
  }

  activateSearchSuggest() {
    this.searchSuggestionsActive = true;
    this.store.dispatch(showShadow());
  }
  changeSearchQuery(query: string) {
    this.searchQuery = query;
  }

  removeRecentObject(event: any, dataObject: DataObject) {
    event.stopPropagation();
    // Client-only: there is no remove endpoint, so this drops the row from the
    // cached list for the rest of the session.
    this.recentObjectsService.remove(dataObject.id);
  }

  goToFeed(workspaceId: string, feedId: string): void {
    this.router.navigate([
      'analytics',
      'workspace',
      workspaceId,
      'feed',
      feedId,
    ]);
  }
  goToDashboard(workspaceId: string, dashboardId: string): void {
    this.router.navigate([
      'analytics',
      'workspace',
      workspaceId,
      'dashboard',
      dashboardId,
    ]);
  }
}
